import React, { useState, useEffect, useMemo } from "react";
import { useBranch } from '../../layouts/BranchContext';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  useTheme,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Typography,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import { tableScrollbarStyles } from "../../layouts/tableScrollbarStyles";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

// ======================================================
// API BASE URL
// ======================================================
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:8000";

// ======================================================
// STATUS OPTIONS
// ======================================================
const STATUS_OPTIONS = [
  {
    value: "P",
    label: "Present",
    shortLabel: "P",
    color: "success",
  },
  {
    value: "A",
    label: "Absent",
    shortLabel: "A",
    color: "error",
  },
  {
    value: "H",
    label: "Half Day",
    shortLabel: "H",
    color: "warning",
  },
  {
    value: "L",
    label: "Leave",
    shortLabel: "L",
    color: "info",
  },
];

// ======================================================
// HELPERS
// ======================================================
const pad2 = (value) =>
  String(value ?? "").padStart(2, "0");

// ======================================================
// Normalize user input
//
// Examples:
// 1       -> 1
// 10      -> 10
// 100     -> 10:0
// 1000    -> 10:00
// 1:      -> 1
// 1:3     -> 1:3
// 01:30   -> 01:30
// ======================================================
const normalize12Input = (value) => {
  const cleaned = String(value ?? "")
    .replace(/[^0-9:]/g, "")
    .slice(0, 5);

  if (!cleaned) return "";

  const digits = cleaned.replace(/:/g, "");

  if (!digits) return "";

  if (cleaned.includes(":")) {
    const [h = "", m = ""] = cleaned.split(":");

    const hour = h.slice(0, 2);
    const minute = m.slice(0, 2);

    if (!minute) {
      return hour;
    }

    return `${hour}:${minute}`;
  }

  if (digits.length <= 2) {
    return digits;
  }

  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
};

// ======================================================
// Convert 12-hour time to 24-hour
//
// 01:00 AM -> 01:00
// 01:00 PM -> 13:00
// 12:00 AM -> 00:00
// 12:00 PM -> 12:00
// ======================================================
const to24Hour = (value, period) => {
  const raw = String(value ?? "").trim();

  if (!raw) return "";

  let [hourText, minuteText = "00"] =
    raw.split(":");

  hourText = hourText.trim();
  minuteText = minuteText.trim();

  // Do not convert incomplete input
  if (!hourText || !minuteText) {
    return "";
  }

  // Must be exactly valid numeric values
  if (!/^\d{1,2}$/.test(hourText)) {
    return "";
  }

  if (!/^\d{1,2}$/.test(minuteText)) {
    return "";
  }

  let hour = Number(hourText);
  let minute = Number(minuteText);

  if (
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return "";
  }

  if (hour < 1 || hour > 12) {
    return "";
  }

  if (minute < 0 || minute > 59) {
    return "";
  }

  const upperPeriod =
    String(period || "AM").toUpperCase();

  if (upperPeriod === "AM") {
    hour = hour === 12 ? 0 : hour;
  } else {
    hour = hour === 12 ? 12 : hour + 12;
  }

  return `${pad2(hour)}:${pad2(minute)}`;
};

// ======================================================
// Convert 24-hour -> 12-hour
// ======================================================
const from24Hour = (value) => {
  if (!value) {
    return {
      value: "",
      period: "AM",
    };
  }

  const raw = String(value).trim();

  const [h, m = "00"] = raw.split(":");

  const hour24 = Number(h);
  const minute = Number(m);

  if (
    !Number.isFinite(hour24) ||
    !Number.isFinite(minute) ||
    hour24 < 0 ||
    hour24 > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return {
      value: "",
      period: "AM",
    };
  }

  const period =
    hour24 >= 12 ? "PM" : "AM";

  const hour12 =
    hour24 % 12 || 12;

  return {
    value: `${pad2(hour12)}:${pad2(minute)}`,
    period,
  };
};

// ======================================================
// Current time
// ======================================================
const getCurrent12Hour = () => {
  const now = new Date();

  return from24Hour(
    `${pad2(now.getHours())}:${pad2(
      now.getMinutes()
    )}`
  );
};

// ======================================================
// Minutes from 24-hour value
// ======================================================
const getMinutesFrom24 = (value) => {
  if (!value) return null;

  const parts = String(value).split(":");

  if (parts.length !== 2) {
    return null;
  }

  const h = Number(parts[0]);
  const m = Number(parts[1]);

  if (
    !Number.isFinite(h) ||
    !Number.isFinite(m)
  ) {
    return null;
  }

  if (
    h < 0 ||
    h > 23 ||
    m < 0 ||
    m > 59
  ) {
    return null;
  }

  return h * 60 + m;
};

// ======================================================
// Get stored user
// ======================================================
const getStoredUser = () => {
  const possibleKeys = [
    "user",
    "currentUser",
    "userData",
    "loggedInUser",
    "profile",
  ];

  for (const storage of [
    sessionStorage,
    localStorage,
  ]) {
    for (const key of possibleKeys) {
      try {
        const value =
          storage.getItem(key);

        if (value) {
          const parsed =
            JSON.parse(value);

          if (
            parsed &&
            typeof parsed === "object"
          ) {
            return parsed;
          }
        }
      } catch (error) {
        // Ignore invalid JSON
      }
    }
  }

  return null;
};

// ======================================================
// Get ID helper
// ======================================================
const getId = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const id = Number(value);

  return Number.isNaN(id)
    ? value
    : id;
};

// ======================================================
// Employee branch ID
// ======================================================
const getEmployeeBranchId = (
  employee
) => {
  return (
    employee?.branch_id ??
    employee?.branchId ??
    employee?.branch?.id ??
    employee?.branch?.branch_id ??
    employee?.branch?.value ??
    employee?.branch ??
    null
  );
};

// ======================================================
// Employee department ID
// ======================================================
const getEmployeeDepartmentId = (
  employee
) => {
  return (
    employee?.department_id ??
    employee?.departmentId ??
    employee?.department?.id ??
    employee?.department?.department_id ??
    employee?.department ??
    null
  );
};

// ======================================================
// Employee department name
// ======================================================
const getEmployeeDepartmentName = (
  employee
) => {
  return (
    employee?.department_name ??
    employee?.department?.name ??
    employee?.department?.department_name ??
    employee?.departmentName ??
    "-"
  );
};

// ======================================================
// Employee name
// ======================================================
const getEmployeeName = (
  employee
) => {
  return (
    employee?.name ||
    employee?.employee_name ||
    employee?.full_name ||
    `${employee?.first_name || ""} ${
      employee?.last_name || ""
    }`.trim() ||
    "-"
  );
};

// ======================================================
// COMPONENT
// ======================================================
const AddEmployeeAttendance = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isDark = theme.palette.mode === "dark";
const idleBorder = isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)";

// for TextFields (Date, Time In, Time Out, Global Remark)
const headerFieldSx = {
  "& .MuiInputLabel-root": {
    color: `${theme.palette.text.primary} !important`,
    opacity: 1,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: `${theme.palette.primary.main} !important`,
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: idleBorder },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
  },
  "& .MuiInputBase-input": {
    color: theme.palette.text.primary,
    WebkitTextFillColor: theme.palette.text.primary,
  },
  "& .MuiInputBase-input::placeholder": {
    color: theme.palette.text.primary,
    opacity: 0.7,
  },
};

// for Selects (Department, AM/PM)
const headerSelectSx = {
  color: theme.palette.text.primary,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: idleBorder },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
  },
  "& .MuiSelect-select": {
    WebkitTextFillColor: theme.palette.text.primary,
  },
  "& .MuiSvgIcon-root": { color: theme.palette.text.primary },
};

  // ======================================================
  // Date
  // ======================================================

  const { selectedBranchId, branchList, isMainBranchAdmin } = useBranch();

  const [date, setDate] = useState(
    new Date()
      .toISOString()
      .split("T")[0]
  );

  // ======================================================
  // Employees
  // ======================================================
  const [employees, setEmployees] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState(null);

  // ======================================================
  // Department
  // ======================================================
  const [
    selectedDepartment,
    setSelectedDepartment,
  ] = useState("");

  // ======================================================
  // Selected employees
  // ======================================================
  const [selectedIds, setSelectedIds] =
    useState([]);

  // ======================================================
  // Attendance
  // ======================================================
  const [attendance, setAttendance] =
    useState({});

  // ======================================================
  // API time values
  //
  // These values are always 24-hour format:
  // 01:00
  // 13:00
  // 17:00
  // ======================================================
  const [timeIn, setTimeIn] =
    useState({});

  const [timeOut, setTimeOut] =
    useState({});

  // ======================================================
  // RAW INPUT VALUES
  //
  // These are what the user is actually typing.
  //
  // Example:
  // "01:00"
  // "1:0"
  // "01:"
  // ""
  //
  // IMPORTANT:
  // This fixes the clear/edit problem.
  // ======================================================
  const [timeInInput, setTimeInInput] =
    useState({});

  const [
    timeOutInput,
    setTimeOutInput,
  ] = useState({});

  // ======================================================
  // Individual periods
  // ======================================================
  const [
    timeInPeriod,
    setTimeInPeriod,
  ] = useState({});

  const [
    timeOutPeriod,
    setTimeOutPeriod,
  ] = useState({});

  // ======================================================
  // Remarks
  // ======================================================
  const [remark, setRemark] =
    useState({});

  // ======================================================
  // Global controls
  // ======================================================
  const [
    globalAttendance,
    setGlobalAttendance,
  ] = useState("P");

  const [
    globalTimeIn,
    setGlobalTimeIn,
  ] = useState("");

  const [
    globalTimeInPeriod,
    setGlobalTimeInPeriod,
  ] = useState("AM");

  const [
    globalTimeOut,
    setGlobalTimeOut,
  ] = useState("");

  const [
    globalTimeOutPeriod,
    setGlobalTimeOutPeriod,
  ] = useState("PM");

  const [
    globalRemark,
    setGlobalRemark,
  ] = useState("");

  // ======================================================
  // Snackbar
  // ======================================================
  const [
    snackbarOpen,
    setSnackbarOpen,
  ] = useState(false);

  const [
    submitLoading,
    setSubmitLoading,
  ] = useState(false);

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState("");

  const [
    snackbarSeverity,
    setSnackbarSeverity,
  ] = useState("success");


  // ======================================================
  // FETCH EMPLOYEES
  // ======================================================
  useEffect(() => {
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError(null);

      const accessToken = localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken");
      const selectedOrganizationId = getOrganizationId();

      console.log("========================================");
      console.log("MARK ATTENDANCE - FILTER DETAILS");
      console.log("Selected Branch ID:", selectedBranchId);
      console.log("Selected Organization ID:", selectedOrganizationId);
      console.log("========================================");

      if (selectedOrganizationId === null) {
        setError("Organization ID not found.");
        setEmployees([]);
        return;
      }

      if (selectedBranchId === null) {
        setError("Branch ID not found.");
        setEmployees([]);
        return;
      }

      const params = {
        organization_id: selectedOrganizationId,
        branch_id: selectedBranchId,
      };

      const response = await axios.get(`${API_BASE_URL}/api/staff/employees/filter/`, {
        params,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      console.log("Employee Filter API Response:", response.data);

      let empList = [];
      if (Array.isArray(response.data)) {
        empList = response.data;
      } else if (Array.isArray(response.data?.results)) {
        empList = response.data.results;
      } else if (Array.isArray(response.data?.data)) {
        empList = response.data.data;
      } else if (Array.isArray(response.data?.employees)) {
        empList = response.data.employees;
      }

      // Safety branch filtering
      const branchFilteredEmployees = empList.filter((emp) => {
        const employeeBranchId = getEmployeeBranchId(emp);
        if (employeeBranchId !== null && employeeBranchId !== undefined) {
          return String(employeeBranchId) === String(selectedBranchId);
        }
        return true;
      });

      empList = branchFilteredEmployees;
      setEmployees(empList);
      setSelectedDepartment("");

      // Initialize attendance, time, and remark states
      const initialAttendance = empList.reduce((acc, emp) => {
        acc[emp.id] = "P";
        return acc;
      }, {});
      setAttendance(initialAttendance);

      const initialTimeIn = empList.reduce((acc, emp) => {
        acc[emp.id] = "";
        return acc;
      }, {});
      setTimeIn(initialTimeIn);

      const initialTimeOut = empList.reduce((acc, emp) => {
        acc[emp.id] = "";
        return acc;
      }, {});
      setTimeOut(initialTimeOut);

      const initialTimeInInput = empList.reduce((acc, emp) => {
        acc[emp.id] = "";
        return acc;
      }, {});
      setTimeInInput(initialTimeInInput);

      const initialTimeOutInput = empList.reduce((acc, emp) => {
        acc[emp.id] = "";
        return acc;
      }, {});
      setTimeOutInput(initialTimeOutInput);

      const initialTimeInPeriod = empList.reduce((acc, emp) => {
        acc[emp.id] = "AM";
        return acc;
      }, {});
      setTimeInPeriod(initialTimeInPeriod);

      const initialTimeOutPeriod = empList.reduce((acc, emp) => {
        acc[emp.id] = "PM";
        return acc;
      }, {});
      setTimeOutPeriod(initialTimeOutPeriod);

      const initialRemark = empList.reduce((acc, emp) => {
        acc[emp.id] = "";
        return acc;
      }, {});
      setRemark(initialRemark);

      setSelectedIds([]);
    } catch (err) {
      console.error("Failed to load employees:", err);
      console.error("Employee API Status:", err.response?.status);
      console.error("Employee API Response:", err.response?.data);

      const backendMessage = err.response?.data?.message || err.response?.data?.detail || "Failed to load employee list. Please try again.";
      setError(backendMessage);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  fetchEmployees();
}, [location.search, location.state, selectedBranchId]);

  // ======================================================
  // Departments
  // ======================================================
  const departments = useMemo(() => {
    const departmentMap =
      new Map();

    employees.forEach(
      (employee) => {
        const departmentId =
          getEmployeeDepartmentId(
            employee
          );

        const departmentName =
          getEmployeeDepartmentName(
            employee
          );

        if (
          departmentId !==
            null &&
          departmentId !==
            undefined &&
          departmentId !== "" &&
          departmentName &&
          departmentName !== "-"
        ) {
          const key =
            String(
              departmentId
            );

          if (
            !departmentMap.has(
              key
            )
          ) {
            departmentMap.set(
              key,
              {
                id: departmentId,
                name:
                  departmentName,
              }
            );
          }
        }
      }
    );

    return Array.from(
      departmentMap.values()
    ).sort((a, b) =>
      String(
        a.name
      ).localeCompare(
        String(b.name)
      )
    );
  }, [employees]);

  // ======================================================
  // Filtered employees
  // ======================================================
  const filteredEmployees =
    useMemo(() => {
      if (
        selectedDepartment ===
          "" ||
        selectedDepartment ===
          null
      ) {
        return employees;
      }

      return employees.filter(
        (employee) => {
          const departmentId =
            getEmployeeDepartmentId(
              employee
            );

          return (
            String(
              departmentId
            ) ===
            String(
              selectedDepartment
            )
          );
        }
      );
    }, [
      employees,
      selectedDepartment,
    ]);

  // ======================================================
  // Apply global values when they change
  // ======================================================
  useEffect(() => {
    if (
      !filteredEmployees.length
    ) {
      return;
    }

    const apiIn = to24Hour(
      globalTimeIn,
      globalTimeInPeriod
    );

    const apiOut = to24Hour(
      globalTimeOut,
      globalTimeOutPeriod
    );

    // ------------------------------
    // IMPORTANT: keep row AM/PM in sync
    // with the global AM/PM controls.
    // Without this, changing global Time Out
    // from AM to PM could leave the row as AM.
    // ------------------------------
    setTimeInPeriod((previous) => {
      const updated = { ...previous };

      filteredEmployees.forEach((emp) => {
        updated[emp.id] = globalTimeInPeriod;
      });

      return updated;
    });

    setTimeOutPeriod((previous) => {
      const updated = { ...previous };

      filteredEmployees.forEach((emp) => {
        updated[emp.id] = globalTimeOutPeriod;
      });

      return updated;
    });

    // ------------------------------
    // Time In
    // ------------------------------
    if (globalTimeIn) {
      setTimeInInput(
        (previous) => {
          const updated = {
            ...previous,
          };

          filteredEmployees.forEach(
            (emp) => {
              updated[emp.id] =
                globalTimeIn;
            }
          );

          return updated;
        }
      );

      if (apiIn) {
        setTimeIn(
          (previous) => {
            const updated = {
              ...previous,
            };

            filteredEmployees.forEach(
              (emp) => {
                updated[emp.id] =
                  apiIn;
              }
            );

            return updated;
          }
        );
      }
    }

    // ------------------------------
    // Time Out
    // ------------------------------
    if (globalTimeOut) {
      setTimeOutInput(
        (previous) => {
          const updated = {
            ...previous,
          };

          filteredEmployees.forEach(
            (emp) => {
              updated[emp.id] =
                globalTimeOut;
            }
          );

          return updated;
        }
      );

      if (apiOut) {
        setTimeOut(
          (previous) => {
            const updated = {
              ...previous,
            };

            filteredEmployees.forEach(
              (emp) => {
                updated[emp.id] =
                  apiOut;
              }
            );

            return updated;
          }
        );
      }
    }

    // ------------------------------
    // Remark
    // ------------------------------
    if (globalRemark) {
      setRemark(
        (previous) => {
          const updated = {
            ...previous,
          };

          filteredEmployees.forEach(
            (emp) => {
              updated[emp.id] =
                globalRemark;
            }
          );

          return updated;
        }
      );
    }
  }, [
    filteredEmployees,
    globalTimeIn,
    globalTimeInPeriod,
    globalTimeOut,
    globalTimeOutPeriod,
    globalRemark,
  ]);

  // ======================================================
  // Department selection cleanup
  // ======================================================
  useEffect(() => {
    const visibleIds =
      new Set(
        filteredEmployees.map(
          (employee) =>
            employee.id
        )
      );

    setSelectedIds(
      (previous) =>
        previous.filter((id) =>
          visibleIds.has(id)
        )
    );
  }, [filteredEmployees]);

  const getOrganizationId = () => {
  const user = getStoredUser();
  return user?.organization_id || user?.organizationId || user?.organization?.id || null;
};

  // ======================================================
  // Current time
  // ======================================================
  const getCurrentTime = () => {
    const current =
      getCurrent12Hour();

    return to24Hour(
      current.value,
      current.period
    );
  };

  // ======================================================
  // Apply global Time In
  // ======================================================
  const applyGlobalTimeIn = (
    value,
    period
  ) => {
    const normalized = normalize12Input(value);

    setGlobalTimeIn(normalized);
    setGlobalTimeInPeriod(period);

    const apiTime = to24Hour(normalized, period);

    setTimeInInput((previous) => {
      const updated = { ...previous };

      filteredEmployees.forEach((emp) => {
        updated[emp.id] = normalized;
      });

      return updated;
    });

    setTimeIn((previous) => {
      const updated = { ...previous };

      filteredEmployees.forEach((emp) => {
        updated[emp.id] = apiTime || "";
      });

      return updated;
    });

    // IMPORTANT: also update every visible row's AM/PM value.
    setTimeInPeriod((previous) => {
      const updated = { ...previous };

      filteredEmployees.forEach((emp) => {
        updated[emp.id] = period;
      });

      return updated;
    });
  };

  // ======================================================
  // Apply global Time Out
  // ======================================================
  const applyGlobalTimeOut = (
    value,
    period
  ) => {
    const normalized = normalize12Input(value);

    setGlobalTimeOut(normalized);
    setGlobalTimeOutPeriod(period);

    const apiTime = to24Hour(normalized, period);

    setTimeOutInput((previous) => {
      const updated = { ...previous };

      filteredEmployees.forEach((emp) => {
        updated[emp.id] = normalized;
      });

      return updated;
    });

    setTimeOut((previous) => {
      const updated = { ...previous };

      filteredEmployees.forEach((emp) => {
        updated[emp.id] = apiTime || "";
      });

      return updated;
    });

    // IMPORTANT: also update every visible row's AM/PM value.
    setTimeOutPeriod((previous) => {
      const updated = { ...previous };

      filteredEmployees.forEach((emp) => {
        updated[emp.id] = period;
      });

      return updated;
    });
  };

  // ======================================================
  // Global attendance
  // ======================================================
  const handleGlobalAttendanceChange =
    (e, nextValue) => {
      const value =
        nextValue ??
        e?.target?.value;

      if (!value) return;

      setGlobalAttendance(
        value
      );

      setAttendance(
        (previous) => {
          const updated = {
            ...previous,
          };

          filteredEmployees.forEach(
            (emp) => {
              updated[emp.id] =
                value;
            }
          );

          return updated;
        }
      );

      // Absent / Leave
      if (
        value === "A" ||
        value === "L"
      ) {
        setTimeInInput(
          (previous) => {
            const updated = {
              ...previous,
            };

            filteredEmployees.forEach(
              (emp) => {
                updated[emp.id] =
                  "";
              }
            );

            return updated;
          }
        );

        setTimeOutInput(
          (previous) => {
            const updated = {
              ...previous,
            };

            filteredEmployees.forEach(
              (emp) => {
                updated[emp.id] =
                  "";
              }
            );

            return updated;
          }
        );

        setTimeIn(
          (previous) => {
            const updated = {
              ...previous,
            };

            filteredEmployees.forEach(
              (emp) => {
                updated[emp.id] =
                  "";
              }
            );

            return updated;
          }
        );

        setTimeOut(
          (previous) => {
            const updated = {
              ...previous,
            };

            filteredEmployees.forEach(
              (emp) => {
                updated[emp.id] =
                  "";
              }
            );

            return updated;
          }
        );
      }

      // Present / Half Day
      if (
        value === "P" ||
        value === "H"
      ) {
        const apiTimeIn =
          to24Hour(
            globalTimeIn,
            globalTimeInPeriod
          );

        const apiTimeOut =
          to24Hour(
            globalTimeOut,
            globalTimeOutPeriod
          );

        if (apiTimeIn) {
          setTimeIn(
            (previous) => {
              const updated = {
                ...previous,
              };

              filteredEmployees.forEach(
                (emp) => {
                  updated[emp.id] =
                    apiTimeIn;
                }
              );

              return updated;
            }
          );
        }

        if (apiTimeOut) {
          setTimeOut(
            (previous) => {
              const updated = {
                ...previous,
              };

              filteredEmployees.forEach(
                (emp) => {
                  updated[emp.id] =
                    apiTimeOut;
                }
              );

              return updated;
            }
          );
        }
      }

      if (globalRemark) {
        setRemark(
          (previous) => {
            const updated = {
              ...previous,
            };

            filteredEmployees.forEach(
              (emp) => {
                updated[emp.id] =
                  globalRemark;
              }
            );

            return updated;
          }
        );
      }
    };

  // ======================================================
  // Global remark
  // ======================================================
  const handleGlobalRemarkChange =
    (value) => {
      setGlobalRemark(value);

      setRemark(
        (previous) => {
          const updated = {
            ...previous,
          };

          filteredEmployees.forEach(
            (emp) => {
              updated[emp.id] =
                value;
            }
          );

          return updated;
        }
      );
    };

  // ======================================================
  // Individual attendance
  // ======================================================
  const handleAttendanceChange = (
    id,
    value
  ) => {
    setAttendance(
      (previous) => ({
        ...previous,
        [id]: value,
      })
    );

    // Present / Half Day
    if (
      (value === "P" ||
        value === "H") &&
      !timeIn[id]
    ) {
      const currentTime =
        getCurrentTime();

      const current12 =
        from24Hour(
          currentTime
        );

      setTimeInInput(
        (previous) => ({
          ...previous,
          [id]:
            current12.value,
        })
      );

      setTimeInPeriod(
        (previous) => ({
          ...previous,
          [id]:
            current12.period,
        })
      );

      setTimeIn(
        (previous) => ({
          ...previous,
          [id]:
            currentTime,
        })
      );
    }

    // Absent / Leave
    if (
      value === "A" ||
      value === "L"
    ) {
      setTimeInInput(
        (previous) => ({
          ...previous,
          [id]: "",
        })
      );

      setTimeOutInput(
        (previous) => ({
          ...previous,
          [id]: "",
        })
      );

      setTimeIn(
        (previous) => ({
          ...previous,
          [id]: "",
        })
      );

      setTimeOut(
        (previous) => ({
          ...previous,
          [id]: "",
        })
      );
    }
  };

  // ======================================================
  // Individual Time In
  // ======================================================
  const handleTimeInChange = (
    id,
    value,
    period
  ) => {
    const normalized =
      normalize12Input(value);

    // IMPORTANT:
    // Keep raw user input separately.
    setTimeInInput(
      (previous) => ({
        ...previous,
        [id]: normalized,
      })
    );

    // API value only when complete/valid
    const apiTime =
      to24Hour(
        normalized,
        period
      );

    setTimeIn(
      (previous) => ({
        ...previous,
        [id]: apiTime || "",
      })
    );

    setTimeInPeriod(
      (previous) => ({
        ...previous,
        [id]: period,
      })
    );
  };

  // ======================================================
  // Individual Time Out
  // ======================================================
  const handleTimeOutChange = (
    id,
    value,
    period
  ) => {
    const normalized =
      normalize12Input(value);

    // Keep raw user input
    setTimeOutInput(
      (previous) => ({
        ...previous,
        [id]: normalized,
      })
    );

    // API value only when valid
    const apiTime =
      to24Hour(
        normalized,
        period
      );

    setTimeOut(
      (previous) => ({
        ...previous,
        [id]: apiTime || "",
      })
    );

    setTimeOutPeriod(
      (previous) => ({
        ...previous,
        [id]: period,
      })
    );
  };

  // ======================================================
  // Time In Period
  // ======================================================
  const handleRowTimeInPeriodChange =
    (id, period) => {
      const currentValue =
        timeInInput[id] || "";

      setTimeInPeriod(
        (previous) => ({
          ...previous,
          [id]: period,
        })
      );

      const apiTime =
        to24Hour(
          currentValue,
          period
        );

      setTimeIn(
        (previous) => ({
          ...previous,
          [id]: apiTime || "",
        })
      );
    };

  // ======================================================
  // Time Out Period
  // ======================================================
  const handleRowTimeOutPeriodChange =
    (id, period) => {
      const currentValue =
        timeOutInput[id] || "";

      setTimeOutPeriod(
        (previous) => ({
          ...previous,
          [id]: period,
        })
      );

      const apiTime =
        to24Hour(
          currentValue,
          period
        );

      setTimeOut(
        (previous) => ({
          ...previous,
          [id]: apiTime || "",
        })
      );
    };

  // ======================================================
  // Remark
  // ======================================================
  const handleRemarkChange = (
    id,
    value
  ) => {
    setRemark(
      (previous) => ({
        ...previous,
        [id]: value,
      })
    );
  };

  // ======================================================
  // Select One
  // ======================================================
  const handleSelectOne = (id) => (
    event
  ) => {
    if (event.target.checked) {
      setSelectedIds(
        (previous) => {
          if (
            previous.includes(id)
          ) {
            return previous;
          }

          return [
            ...previous,
            id,
          ];
        }
      );
    } else {
      setSelectedIds(
        (previous) =>
          previous.filter(
            (selectedId) =>
              selectedId !== id
          )
      );
    }
  };

  // ======================================================
  // Select All
  // ======================================================
  const handleSelectAll = (
    event
  ) => {
    if (event.target.checked) {
      const visibleIds =
        filteredEmployees.map(
          (employee) =>
            employee.id
        );

      setSelectedIds(
        (previous) => {
          const otherSelectedIds =
            previous.filter(
              (id) =>
                !filteredEmployees.some(
                  (employee) =>
                    employee.id ===
                    id
                )
            );

          return [
            ...otherSelectedIds,
            ...visibleIds,
          ];
        }
      );
    } else {
      setSelectedIds(
        (previous) =>
          previous.filter(
            (id) =>
              !filteredEmployees.some(
                (employee) =>
                  employee.id === id
              )
          )
      );
    }
  };

  // ======================================================
  // Select all state
  // ======================================================
  const allVisibleSelected =
    filteredEmployees.length >
      0 &&
    filteredEmployees.every(
      (employee) =>
        selectedIds.includes(
          employee.id
        )
    );

  const someVisibleSelected =
    filteredEmployees.some(
      (employee) =>
        selectedIds.includes(
          employee.id
        )
    );

  // ======================================================
  // SUBMIT
  // ======================================================
  const handleSubmit = async () => {
    if (
      selectedIds.length === 0
    ) {
      setSnackbarMessage(
        "Please select at least one employee"
      );

      setSnackbarSeverity(
        "warning"
      );

      setSnackbarOpen(true);

      return;
    }

    // ==================================================
    // Build records
    // ==================================================
    let records;

    try {
      records = employees
        .filter((employee) =>
          selectedIds.includes(
            employee.id
          )
        )
        .map((employee) => {
          const attendanceValue =
            attendance[
              employee.id
            ] || "P";

          let status =
            "present";

          if (
            attendanceValue ===
            "A"
          ) {
            status = "absent";
          } else if (
            attendanceValue ===
            "H"
          ) {
            status = "half_day";
          } else if (
            attendanceValue ===
            "L"
          ) {
            status = "on_leave";
          }

          // ----------------------------------------------
          // Raw input
          // ----------------------------------------------
          const inRaw =
            timeInInput[
              employee.id
            ] || "";

          const outRaw =
            timeOutInput[
              employee.id
            ] || "";

          // ----------------------------------------------
          // Period
          // ----------------------------------------------
          const inPeriod =
            timeInPeriod[
              employee.id
            ] || "AM";

          const outPeriod =
            timeOutPeriod[
              employee.id
            ] || "PM";

          // ----------------------------------------------
          // Convert ONLY at submit time
          // ----------------------------------------------
          let normalizedIn = "";
          let normalizedOut = "";

          if (
            status === "present" ||
            status === "half_day"
          ) {
            // Time In is required
            normalizedIn =
              to24Hour(
                inRaw,
                inPeriod
              );

            if (!normalizedIn) {
              throw new Error(
                `Please enter a valid Time In for ${getEmployeeName(
                  employee
                )}. Example: 01:00 AM`
              );
            }

            // Time Out is optional
            if (outRaw) {
              normalizedOut =
                to24Hour(
                  outRaw,
                  outPeriod
                );

              if (!normalizedOut) {
                throw new Error(
                  `Please enter a valid Time Out for ${getEmployeeName(
                    employee
                  )}. Example: 05:00 PM`
                );
              }
            }
          }

          // ----------------------------------------------
          // Validate time order
          // ----------------------------------------------
          if (
            normalizedIn &&
            normalizedOut
          ) {
            const inMinutes =
              getMinutesFrom24(
                normalizedIn
              );

            const outMinutes =
              getMinutesFrom24(
                normalizedOut
              );

            if (
              inMinutes === null ||
              outMinutes === null
            ) {
              throw new Error(
                `Invalid time for ${getEmployeeName(
                  employee
                )}.`
              );
            }

            if (
              outMinutes <=
              inMinutes
            ) {
              throw new Error(
                `Time Out (${outRaw} ${outPeriod}) must be greater than Time In (${inRaw} ${inPeriod}) for ${getEmployeeName(
                  employee
                )}.`
              );
            }
          }

          // ----------------------------------------------
          // IMPORTANT:
          // Never send raw input such as "61:1"
          // ----------------------------------------------
          return {
            employee:
              employee.id,

            status,

            time_in:
              status ===
                "present" ||
              status ===
                "half_day"
                ? normalizedIn
                : null,

            time_out:
              status ===
                "present" ||
              status ===
                "half_day"
                ? normalizedOut ||
                  null
                : null,

            remark:
              remark[
                employee.id
              ] || null,
          };
        });
    } catch (validationError) {
      console.error(
        "Attendance validation error:",
        validationError
      );

      setSnackbarMessage(
        validationError.message ||
          "Please enter valid attendance time."
      );

      setSnackbarSeverity(
        "warning"
      );

      setSnackbarOpen(true);

      return;
    }

    // ==================================================
    // Payload
    // ==================================================
    const payload = {
      date,
      records,
    };

    console.log(
      "========================================"
    );

    console.log(
      "ATTENDANCE PAYLOAD"
    );

    console.log(
      JSON.stringify(
        payload,
        null,
        2
      )
    );

    console.log(
      "========================================"
    );

    // ==================================================
    // Extra safety validation
    // ==================================================
    const invalidPayloadTime =
      records.find(
        (record) => {
          if (
            record.time_in &&
            !/^\d{2}:\d{2}$/.test(
              record.time_in
            )
          ) {
            return true;
          }

          if (
            record.time_out &&
            !/^\d{2}:\d{2}$/.test(
              record.time_out
            )
          ) {
            return true;
          }

          return false;
        }
      );

    if (invalidPayloadTime) {
      setSnackbarMessage(
        "Invalid time format detected. Please use HH:MM."
      );

      setSnackbarSeverity(
        "warning"
      );

      setSnackbarOpen(true);

      return;
    }

    // ==================================================
    // API
    // ==================================================
    setSubmitLoading(true);
    setSnackbarOpen(false);

    try {
      const accessToken =
        localStorage.getItem(
          "accessToken"
        ) ||
        sessionStorage.getItem(
          "accessToken"
        );

      const response =
        await axios.post(
          `${API_BASE_URL}/api/staff/attendance/`,
          payload,
          {
            headers: {
              "Content-Type":
                "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

      console.log(
        "Attendance saved:",
        response.data
      );

    // ==================================================
// SUCCESS
// ==================================================
const employeeNames = records
  .map((record) => {
    const employee = employees.find(
      (emp) =>
        String(emp.id) === String(record.employee)
    );

    return employee
      ? getEmployeeName(employee)
      : `Employee ID ${record.employee}`;
  })
  .filter(Boolean);

setSnackbarMessage(
  `Attendance marked for ${employeeNames.join(", ")} successfully!`
);

setSnackbarSeverity("success");
setSnackbarOpen(true);
      // ==================================================
      // Reset selection
      // ==================================================
      setSelectedIds([]);

      // ==================================================
      // Reset Attendance
      // ==================================================
      setAttendance(
        employees.reduce(
          (acc, employee) => {
            acc[employee.id] =
              "P";

            return acc;
          },
          {}
        )
      );

      // ==================================================
      // Reset Time In
      // ==================================================
      setTimeIn(
        employees.reduce(
          (acc, employee) => {
            acc[employee.id] =
              "";

            return acc;
          },
          {}
        )
      );

      // ==================================================
      // Reset Time Out
      // ==================================================
      setTimeOut(
        employees.reduce(
          (acc, employee) => {
            acc[employee.id] =
              "";

            return acc;
          },
          {}
        )
      );

      // ==================================================
      // Reset raw Time In
      // ==================================================
      setTimeInInput(
        employees.reduce(
          (acc, employee) => {
            acc[employee.id] =
              "";

            return acc;
          },
          {}
        )
      );

      // ==================================================
      // Reset raw Time Out
      // ==================================================
      setTimeOutInput(
        employees.reduce(
          (acc, employee) => {
            acc[employee.id] =
              "";

            return acc;
          },
          {}
        )
      );

      // ==================================================
      // Reset periods
      // ==================================================
      setTimeInPeriod(
        employees.reduce(
          (acc, employee) => {
            acc[employee.id] =
              "AM";

            return acc;
          },
          {}
        )
      );

      setTimeOutPeriod(
        employees.reduce(
          (acc, employee) => {
            acc[employee.id] =
              "PM";

            return acc;
          },
          {}
        )
      );

      // ==================================================
      // Reset remarks
      // ==================================================
      setRemark(
        employees.reduce(
          (acc, employee) => {
            acc[employee.id] =
              "";

            return acc;
          },
          {}
        )
      );

      // ==================================================
      // Reset global
      // ==================================================
      setGlobalAttendance("P");
      setGlobalTimeIn("");
      setGlobalTimeInPeriod("AM");
      setGlobalTimeOut("");
      setGlobalTimeOutPeriod("PM");
      setGlobalRemark("");
    } catch (err) {
      console.log(
        "Status:",
        err.response?.status
      );

      console.log(
        "Response:",
        err.response?.data
      );

      const data =
        err.response?.data;

      let errorMsg =
        "Failed to mark attendance";

     if (data?.non_field_errors?.length > 0) {
  errorMsg = data.non_field_errors
    .map((message) => {
      // Backend message:
      // "Attendance already exists for employee(s): [20]"

      const match = String(message).match(
        /Attendance already exists for employee\(s\):\s*\[([^\]]+)\]/i
      );

      if (!match) {
        return message;
      }

      const employeeIds = match[1]
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      const employeeNames = employeeIds.map((employeeId) => {
        const employee = employees.find(
          (emp) => String(emp.id) === String(employeeId)
        );

        return employee
          ? getEmployeeName(employee)
          : `Employee ID ${employeeId}`;
      });

      return `Attendance already exists for: ${employeeNames.join(
        ", "
      )}`;
    })
    .join(", ");
} else if (
        data?.message
      ) {
        errorMsg =
          data.message;
      } else if (
        data?.detail
      ) {
        errorMsg =
          data.detail;
      } else if (
        data?.time_in
      ) {
        errorMsg =
          Array.isArray(
            data.time_in
          )
            ? data.time_in.join(
                ", "
              )
            : String(
                data.time_in
              );
      } else if (
        data?.time_out
      ) {
        errorMsg =
          Array.isArray(
            data.time_out
          )
            ? data.time_out.join(
                ", "
              )
            : String(
                data.time_out
              );
      } else if (
        typeof data ===
        "string"
      ) {
        errorMsg = data;
      } else if (
        err.message
      ) {
        errorMsg =
          err.message;
      }

      setSnackbarMessage(
        errorMsg
      );

      setSnackbarSeverity(
        "error"
      );

      setSnackbarOpen(true);
    } finally {
      setSubmitLoading(false);
    }
  };

  // ======================================================
  // Snackbar close
  // ======================================================
  const handleCloseSnackbar =
    () => {
      setSnackbarOpen(false);
    };

  // ======================================================
  // UI
  // ======================================================
  return (
    <Container maxWidth="xl">
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 4,
        }}
      >
        {/* ==================================================
            HEADER
        ================================================== */}
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "stretch",
              md: "center",
            },
            justifyContent:
              "space-between",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {/* TITLE */}
          <Typography
            variant="h6"
            sx={{
              flex: 1,
              minWidth: {
                xs: "100%",
                md: "auto",
              },
              textAlign: "left",
              color:
                theme.palette.primary
                  .main,
              fontWeight: 600,
            }}
          >
            Employee List
          </Typography>

          {/* DATE */}
          <TextField
            type="date"
            label="Date"
            value={date}
            onChange={(e) =>
              setDate(
                e?.target?.value ??
                  ""
              )
            }
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              max: new Date()
                .toISOString()
                .split("T")[0],
            }}
           sx={{
  ...headerFieldSx,
  minWidth: 180,
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: theme.palette.background.paper,
  },
  "& .MuiOutlinedInput-input": { padding: "11px 18px" },
  "& input[type='date']::-webkit-calendar-picker-indicator": {
    filter: isDark ? "invert(1)" : "none",
    cursor: "pointer",
  },
}}
          />

          {/* DEPARTMENT */}
          <FormControl
            size="small"
            sx={{
              minWidth: {
                xs: "100%",
                sm: 220,
              },
            }}
          >
           <InputLabel
  shrink
  sx={{
    color: `${theme.palette.text.primary} !important`,
    "&.Mui-focused": { color: `${theme.palette.primary.main} !important` },
  }}
>
  Department
</InputLabel>

<Select
  value={selectedDepartment}
  label="Department"
  displayEmpty
  notched
  sx={{
    ...headerSelectSx,
    borderRadius: "10px",
    backgroundColor: theme.palette.background.paper,
  }}
            >
              <MenuItem value="">
                All Departments
              </MenuItem>

              {(
                Array.isArray(
                  departments
                )
                  ? departments
                  : []
              ).map(
                (department) => (
                  <MenuItem
                    key={
                      department.id
                    }
                    value={
                      department.id
                    }
                  >
                    {
                      department.name
                    }
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>

          {/* ==================================================
              GLOBAL CONTROLS
          ================================================== */}
          <Box
            sx={{
              display: "flex",
              alignItems:
                "center",
              gap: 2,
              flexWrap:
                "wrap",
              mt: 1,
              width: "100%",
            }}
          >
            {/* Global Attendance */}
            <ToggleButtonGroup
              value={
                globalAttendance
              }
              exclusive
              size="small"
              onChange={(
                e,
                value
              ) => {
                if (
                  value !==
                  null
                ) {
                  handleGlobalAttendanceChange(
                    e,
                    value
                  );
                }
              }}
            >
              {STATUS_OPTIONS.map(
                (opt) => {
                  const paletteColor =
                    theme.palette[
                      opt.color
                    ] ||
                    theme.palette
                      .primary;

                  return (
                    <ToggleButton
                      key={
                        opt.value
                      }
                      value={
                        opt.value
                      }
                      sx={{
                        minWidth: {
                          xs: 58,
                          sm: 72,
                        },
                        px: 1.2,
                        fontWeight: 700,
                        textTransform:
                          "none",
                        color:
                          paletteColor.main,
                        borderColor:
                          theme.palette
                            .divider,

                        "&:hover":
                          {
                            backgroundColor:
                              paletteColor.light,
                            color:
                              paletteColor.dark,
                          },

                        "&.Mui-selected":
                          {
                            backgroundColor:
                              paletteColor.main,
                            color:
                              "#fff",
                            borderColor:
                              paletteColor.main,

                            "&:hover":
                              {
                                backgroundColor:
                                  paletteColor.dark,
                                color:
                                  "#fff",
                              },
                          },
                      }}
                    >
                      {
                        opt.label
                      }
                    </ToggleButton>
                  );
                }
              )}
            </ToggleButtonGroup>

            {/* Global Time In */}
            <Box
              sx={{
                display:
                  "flex",
                alignItems:
                  "center",
                gap: 0.5,
              }}
            >
              <TextField
                label="Time In"
                size="small"
                value={
                  globalTimeIn
                }
                onChange={(
                  e
                ) =>
                  applyGlobalTimeIn(
                    e.target
                      .value,
                    globalTimeInPeriod
                  )
                }
                placeholder="11 or 11:00"
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  inputMode:
                    "numeric",
                }}
                sx={{ ...headerFieldSx, width: 125 }}
              />

              <Select
                size="small"
                value={
                  globalTimeInPeriod
                }
                onChange={(
                  e
                ) =>
                  applyGlobalTimeIn(
                    globalTimeIn,
                    e.target
                      .value
                  )
                }
                sx={{ ...headerSelectSx, minWidth: 85, height: 40 }}
              >
                <MenuItem value="AM">
                  AM
                </MenuItem>

                <MenuItem value="PM">
                  PM
                </MenuItem>
              </Select>
            </Box>

            {/* Global Time Out */}
            <Box
              sx={{
                display:
                  "flex",
                alignItems:
                  "center",
                gap: 0.5,
              }}
            >
              <TextField
                label="Time Out"
                size="small"
                value={
                  globalTimeOut
                }
                onChange={(
                  e
                ) =>
                  applyGlobalTimeOut(
                    e.target
                      .value,
                    globalTimeOutPeriod
                  )
                }
                placeholder="6 or 6:00"
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  inputMode:
                    "numeric",
                }}
               sx={{ ...headerFieldSx, width: 125 }}
              />

              <Select
                size="small"
                value={
                  globalTimeOutPeriod
                }
                onChange={(
                  e
                ) =>
                  applyGlobalTimeOut(
                    globalTimeOut,
                    e.target
                      .value
                  )
                }
                sx={{ ...headerSelectSx, minWidth: 85, height: 40 }}
              >
                <MenuItem value="AM">
                  AM
                </MenuItem>

                <MenuItem value="PM">
                  PM
                </MenuItem>
              </Select>
            </Box>

            {/* Global Remark */}
            <TextField
              label="Global Remark"
              size="small"
              value={
                globalRemark
              }
              onChange={(e) =>
                handleGlobalRemarkChange(
                  e.target.value
                )
              }
              placeholder="Apply remark to all"
             InputLabelProps={{ shrink: true }}
sx={{ ...headerFieldSx, minWidth: 200, flex: 1 }}
            />
          </Box>
        </Box>

        {/* ==================================================
            EMPLOYEE TABLE
        ================================================== */}
        <TableContainer
          sx={{
            width: "100%",
            maxHeight:
              "calc(100vh - 390px)",
            ...tableScrollbarStyles,
          }}
        >
          <Table
            stickyHeader
            sx={{
              minWidth: 1200,
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>
                  Serial
                </TableCell>

                <TableCell>
                  <Checkbox
                    indeterminate={
                      someVisibleSelected &&
                      !allVisibleSelected
                    }
                    checked={
                      allVisibleSelected
                    }
                    onChange={
                      handleSelectAll
                    }
                    sx={{
                      color:
                        theme.palette
                          .text
                          .secondary,

                      "&.Mui-checked":
                        {
                          color:
                            theme.palette
                              .primary
                              .main,
                        },

                      "&.MuiCheckbox-indeterminate":
                        {
                          color:
                            theme.palette
                              .primary
                              .main,
                        },
                    }}
                  />

                  Select
                </TableCell>

                <TableCell>
                  Employee Name
                </TableCell>

                <TableCell>
                  Employee Code
                </TableCell>

                <TableCell>
                  Department
                </TableCell>

                <TableCell>
                  Mobile No
                </TableCell>

                <TableCell>
                  Attendance
                </TableCell>

                <TableCell>
                  Time In
                </TableCell>

                <TableCell>
                  Time Out
                </TableCell>

                <TableCell>
                  Remark
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    align="center"
                    sx={{
                      py: 5,
                    }}
                  >
                    Loading employees...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    align="center"
                    sx={{
                      color:
                        theme.palette
                          .error
                          .main,
                      py: 5,
                    }}
                  >
                    {error}
                  </TableCell>
                </TableRow>
              ) : filteredEmployees.length ===
                0 ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    align="center"
                    sx={{
                      py: 5,
                    }}
                  >
                    No employees
                    found for
                    the selected
                    department.
                  </TableCell>
                </TableRow>
              ) : (
                (
                  Array.isArray(
                    filteredEmployees
                  )
                    ? filteredEmployees
                    : []
                ).map(
                  (
                    emp,
                    index
                  ) => {
                    const currentInPeriod =
                      timeInPeriod[
                        emp.id
                      ] ||
                      "AM";

                    const currentOutPeriod =
                      timeOutPeriod[
                        emp.id
                      ] ||
                      "PM";

                    return (
                      <TableRow
                        key={
                          emp.id
                        }
                      >
                        {/* Serial */}
                        <TableCell>
                          {
                            index +
                              1
                          }
                        </TableCell>

                        {/* Select */}
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.includes(
                              emp.id
                            )}
                            onChange={handleSelectOne(
                              emp.id
                            )}
                            sx={{
                              color:
                                theme.palette
                                  .text
                                  .secondary,

                              "&.Mui-checked":
                                {
                                  color:
                                    theme.palette
                                      .primary
                                      .main,
                                },
                            }}
                          />
                        </TableCell>

                        {/* Employee Name */}
                        <TableCell>
                          {getEmployeeName(
                            emp
                          )}
                        </TableCell>

                        {/* Employee Code */}
                        <TableCell>
                          {emp.employee_code ||
                            emp.employeeCode ||
                            emp.code ||
                            "-"}
                        </TableCell>

                        {/* Department */}
                        <TableCell>
                          {getEmployeeDepartmentName(
                            emp
                          )}
                        </TableCell>

                        {/* Mobile */}
                        <TableCell>
                          {emp.mobile ||
                            emp.mobile_number ||
                            emp.phone ||
                            "-"}
                        </TableCell>

                        {/* Attendance */}
                        <TableCell>
                          <ToggleButtonGroup
                            value={
                              attendance[
                                emp.id
                              ] ||
                              "P"
                            }
                            exclusive
                            size="small"
                            onChange={(
                              e,
                              value
                            ) => {
                              if (
                                value !==
                                null
                              ) {
                                handleAttendanceChange(
                                  emp.id,
                                  value
                                );
                              }
                            }}
                          >
                            {STATUS_OPTIONS.map(
                              (
                                opt
                              ) => {
                                const paletteColor =
                                  theme.palette[
                                    opt.color
                                  ] ||
                                  theme.palette
                                    .primary;

                                return (
                                  <ToggleButton
                                    key={
                                      opt.value
                                    }
                                    value={
                                      opt.value
                                    }
                                    sx={{
                                      minWidth: 36,
                                      fontWeight: 700,
                                      px: 1,
                                      color:
                                        paletteColor.main,
                                      borderColor:
                                        theme.palette
                                          .divider,

                                      "&:hover":
                                        {
                                          backgroundColor:
                                            paletteColor.light,
                                        },

                                      "&.Mui-selected":
                                        {
                                          backgroundColor:
                                            paletteColor.main,
                                          color:
                                            "#fff",
                                          borderColor:
                                            paletteColor.main,

                                          "&:hover":
                                            {
                                              backgroundColor:
                                                paletteColor.dark,
                                              color:
                                                "#fff",
                                            },
                                        },
                                    }}
                                  >
                                    <Tooltip
                                      title={
                                        opt.label
                                      }
                                    >
                                      <span>
                                        {
                                          opt.shortLabel
                                        }
                                      </span>
                                    </Tooltip>
                                  </ToggleButton>
                                );
                              }
                            )}
                          </ToggleButtonGroup>
                        </TableCell>

                        {/* ==================================================
                            TIME IN
                        ================================================== */}
                        <TableCell>
                          <Box
                            sx={{
                              display:
                                "flex",
                              alignItems:
                                "center",
                              gap: 0.5,
                            }}
                          >
                            <TextField
                              size="small"

                              // IMPORTANT:
                              // Raw input is rendered.
                              // User can freely clear/edit it.
                              value={
                                timeInInput[
                                  emp.id
                                ] ||
                                ""
                              }

                              onChange={(
                                e
                              ) => {
                                handleTimeInChange(
                                  emp.id,
                                  e.target
                                    .value,
                                  currentInPeriod
                                );
                              }}

                              placeholder="11 or 11:00"

                              disabled={
                                attendance[
                                  emp.id
                                ] ===
                                  "A" ||
                                attendance[
                                  emp.id
                                ] ===
                                  "L"
                              }

                              inputProps={{
                                inputMode:
                                  "numeric",
                              }}

                              sx={{
                                minWidth:
                                  105,
                              }}

                              InputProps={{
                                startAdornment:
                                  (
                                    <InputAdornment position="start">
                                      <AccessTimeIcon
                                        sx={{
                                          fontSize:
                                            17,
                                          color:
                                            theme
                                              .palette
                                              .mode ===
                                            "dark"
                                              ? "#fff"
                                              : theme
                                                  .palette
                                                  .text
                                                  .secondary,
                                        }}
                                      />
                                    </InputAdornment>
                                  ),
                              }}
                            />

                            <Select
                              size="small"
                              value={
                                currentInPeriod
                              }
                              onChange={(
                                e
                              ) =>
                                handleRowTimeInPeriodChange(
                                  emp.id,
                                  e.target
                                    .value
                                )
                              }
                              disabled={
                                attendance[
                                  emp.id
                                ] ===
                                  "A" ||
                                attendance[
                                  emp.id
                                ] ===
                                  "L"
                              }
                              sx={{
                                minWidth:
                                  68,
                                height: 40,
                              }}
                            >
                              <MenuItem value="AM">
                                AM
                              </MenuItem>

                              <MenuItem value="PM">
                                PM
                              </MenuItem>
                            </Select>
                          </Box>
                        </TableCell>

                        {/* ==================================================
                            TIME OUT
                        ================================================== */}
                        <TableCell>
                          <Box
                            sx={{
                              display:
                                "flex",
                              alignItems:
                                "center",
                              gap: 0.5,
                            }}
                          >
                            <TextField
                              size="small"

                              // IMPORTANT:
                              // Raw input is rendered.
                              value={
                                timeOutInput[
                                  emp.id
                                ] ||
                                ""
                              }

                              onChange={(
                                e
                              ) => {
                                handleTimeOutChange(
                                  emp.id,
                                  e.target
                                    .value,
                                  currentOutPeriod
                                );
                              }}

                              placeholder="6 or 6:00"

                              disabled={
                                attendance[
                                  emp.id
                                ] ===
                                  "A" ||
                                attendance[
                                  emp.id
                                ] ===
                                  "L"
                              }

                              inputProps={{
                                inputMode:
                                  "numeric",
                              }}

                              sx={{
                                minWidth:
                                  105,
                              }}
                            />

                            <Select
                              size="small"
                              value={
                                currentOutPeriod
                              }
                              onChange={(
                                e
                              ) =>
                                handleRowTimeOutPeriodChange(
                                  emp.id,
                                  e.target
                                    .value
                                )
                              }
                              disabled={
                                attendance[
                                  emp.id
                                ] ===
                                  "A" ||
                                attendance[
                                  emp.id
                                ] ===
                                  "L"
                              }
                              sx={{
                                minWidth:
                                  68,
                                height: 40,
                              }}
                            >
                              <MenuItem value="AM">
                                AM
                              </MenuItem>

                              <MenuItem value="PM">
                                PM
                              </MenuItem>
                            </Select>
                          </Box>
                        </TableCell>

                        {/* Remark */}
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter remark"
                            value={
                              remark[
                                emp.id
                              ] || ""
                            }
                            onChange={(
                              e
                            ) =>
                              handleRemarkChange(
                                emp.id,
                                e?.target
                                  ?.value ??
                                  ""
                              )
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  }
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ==================================================
            BUTTONS
        ================================================== */}
        <Box
          sx={{
            display: "flex",
            justifyContent:
              "flex-end",
            gap: 2,
            mt: 3,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            onClick={() =>
              navigate(
                "/employee-attendance"
              )
            }
          >
            BACK TO EMPLOYEE ATTENDANCE
          </Button>

          <Button
            variant="contained"
            onClick={
              handleSubmit
            }
            disabled={
              submitLoading ||
              loading ||
              employees.length ===
                0
            }
          >
            {submitLoading
              ? "Saving..."
              : "MARK ATTENDANCE"}
          </Button>
        </Box>
      </Paper>

      {/* ==================================================
          SNACKBAR
      ================================================== */}
      <Snackbar
        open={
          snackbarOpen
        }
        autoHideDuration={
          5000
        }
        onClose={
          handleCloseSnackbar
        }
        anchorOrigin={{
          vertical:
            "bottom",
          horizontal:
            "center",
        }}
      >
        <Alert
          onClose={
            handleCloseSnackbar
          }
          severity={
            snackbarSeverity
          }
          variant="filled"
          sx={{
            width: "100%",
          }}
        >
          {
            snackbarMessage
          }
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddEmployeeAttendance;